import { normalizeState } from "@/pages/pipelineHelpers";
import type { JobMetadata } from "@/types/api";

/**
 * Tracks the user's last Start/Stop *intent* per pipeline until the backend
 * actually converges to it.
 *
 * Why this exists: in an HA deployment the cluster applies start/stop
 * asynchronously, and — crucially — even `desired_state` does not flip
 * synchronously. For a brief window after the request, `GET /jobs` still
 * reports the *old* settled `state`/`desired_state` (the intent hasn't
 * propagated through valkey/leader yet). The mutation's `onSettled` refetch
 * races into that window and, with a cache-only optimistic write, overwrites
 * the transitional state back to the old settled one — re-enabling the button
 * and showing the old badge, so the click looks like it did nothing.
 *
 * Keeping the intent in a module-level store (outside the React Query cache)
 * makes it survive every refetch: we overlay it onto the jobs list until the
 * job's real `state` reaches the intent (or fails), then prune it. Because it
 * is module-level it is shared by the list, the detail page and the dashboard
 * with no provider plumbing, via `useSyncExternalStore`.
 */
export type PipelineIntent = "Running" | "Stopped";

// Backstop: if the backend never converges to (and never fails on) the intent,
// stop overriding after this long so the controls can't lock forever — control
// then falls back to the `state`/`desired_state` divergence logic, which by
// then reflects the real backend transition. Comfortably longer than a normal
// HA start/stop, which converges in seconds.
const MAX_PENDING_MS = 60_000;

const intents = new Map<string, PipelineIntent>();
const setAt = new Map<string, number>();
const listeners = new Set<() => void>();

// useSyncExternalStore requires a stable snapshot reference between emits.
let snapshot: Record<string, PipelineIntent> = {};

function emit(): void {
  snapshot = Object.fromEntries(intents);
  for (const listener of listeners) listener();
}

export function subscribePipelineIntents(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPipelineIntentsSnapshot(): Record<string, PipelineIntent> {
  return snapshot;
}

export function setPipelineIntent(
  name: string,
  intent: PipelineIntent,
  now: number = Date.now(),
): void {
  intents.set(name, intent);
  setAt.set(name, now);
  emit();
}

export function clearPipelineIntent(name: string): void {
  setAt.delete(name);
  if (intents.delete(name)) emit();
}

/** Drop every intent — call on logout so a pending intent can't carry into the
 * next signed-in session (overlaying its jobs UI and keeping fast polling on). */
export function clearAllPipelineIntents(): void {
  if (intents.size === 0) return;
  intents.clear();
  setAt.clear();
  emit();
}

/**
 * Drop intents the backend has resolved or that can no longer converge. Call
 * when fresh jobs data arrives so a stale intent can't keep overriding a job.
 * An intent is cleared once the job:
 *  - reached the target state, or ended in `Failed` (settled), or
 *  - vanished from a non-empty list (deleted externally — guarded against a
 *    transient empty fetch, which would otherwise drop valid intents), or
 *  - has been pending past MAX_PENDING_MS (backend never converged — don't lock
 *    the controls, or keep fast-polling, forever).
 */
export function reconcilePipelineIntents(
  jobs: JobMetadata[],
  now: number = Date.now(),
): void {
  const states = new Map(jobs.map((j) => [j.name, normalizeState(j.state)]));
  let changed = false;
  for (const [name, want] of intents) {
    const state = states.get(name);
    const expired = now - (setAt.get(name) ?? now) > MAX_PENDING_MS;
    const vanished = jobs.length > 0 && state === undefined;
    if (state === want || state === "Failed" || vanished || expired) {
      intents.delete(name);
      setAt.delete(name);
      changed = true;
    }
  }
  if (changed) emit();
}

/**
 * Overlay active intents onto the jobs list: while a job's real `state` has not
 * yet reached the intended target, present it as transitional
 * (`starting`/`stopping`) with `desired_state` set to the target, so the badge
 * and the Start/Stop buttons reflect the in-flight transition regardless of
 * what a racing refetch returned. Converged/failed jobs pass through unchanged
 * (and are pruned by reconcile).
 */
export function overlayPipelineIntents(
  jobs: JobMetadata[],
  active: Record<string, PipelineIntent>,
): JobMetadata[] {
  if (Object.keys(active).length === 0) return jobs;
  return jobs.map((job) => {
    const want = active[job.name];
    if (!want) return job;
    const state = normalizeState(job.state);
    if (state === want || state === "Failed") return job;
    return want === "Running"
      ? { ...job, state: "starting", desired_state: "running" }
      : { ...job, state: "stopping", desired_state: "stopped" };
  });
}
